/**
 * Per-seller BRAND themes — one visual identity per seller, so each storefront in the
 * Sellers panel reads like its own product, not a generic panel.
 *
 * Distilled (via a parallel design-agent pass) from real brand design languages in the
 * VoltAgent `awesome-design-md` DESIGN.md collection
 * (https://github.com/VoltAgent/awesome-design-md), each mapped to a seller whose
 * domain fits the brand. Tokens are web-safe (no external font loading).
 *
 * Token roles (the storefront renders a dark/brand masthead over white content cards):
 *   bg       masthead/hero background
 *   ink      text ON the masthead (auto-derived light/dark from bg luminance)
 *   surface  card background (≈white)
 *   accent   the brand's signature color — used for CTAs/price tags/borders on cards;
 *            corrected to be LEGIBLE on a white surface (no black-on-white "accents")
 *   accent2  secondary/contrast color
 *   border   hairline on cards
 *   radius   card corner radius (px)
 *
 * Consumed by every storefront via getTheme(sellerId). Adding a seller = add one entry.
 */

// Relative luminance of a #rrggbb color (0=black … 1=white).
function luminance(hex) {
  const h = String(hex || '').replace('#', '');
  if (h.length < 6) return 1;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// Pick legible ink for a given background.
export function inkFor(bg) {
  return luminance(bg) < 0.5 ? '#ffffff' : '#16191f';
}

// Raw distilled brand palettes. `accent` here is already corrected for white-card use.
const RAW = {
  // The Emporium — Shopify: cinematic black masthead, growth-green accent.
  shop: { brand: 'Shopify', name: 'The Emporium', kind: 'Retail',
    accent: '#008060', accent2: '#95bf47', bg: '#0a0a0a', surface: '#ffffff',
    muted: '#9dabad', border: '#e4e4e7', headingFont: "'Helvetica Neue', Helvetica, Arial, sans-serif", radius: 12,
    signature: 'Cinematic near-black masthead, Shopify-green CTAs, clean white product cards.' },
  // The Continental — The Verge: industrial editorial, jelly-mint + ultraviolet.
  publisher: { brand: 'The Verge', name: 'The Continental', kind: 'News',
    accent: '#5200ff', accent2: '#7c2128', bg: '#131313', surface: '#fffdf8',
    muted: '#7a7064', border: '#e2dac9', headingFont: "'Arial Narrow', 'Helvetica Neue', Impact, sans-serif", radius: 14,
    signature: 'Near-black masthead with heavy all-caps wordmark, ultraviolet accents, flat hairline cards.' },
  // The Marquee — Spotify: dark stage so artwork glows, single green accent.
  marquee: { brand: 'Spotify', name: 'The Marquee', kind: 'Movies & TV',
    accent: '#1db954', accent2: '#caa34a', bg: '#121212', surface: '#181818',
    muted: '#b3b3b3', border: '#282828', headingFont: "'Helvetica Neue', Helvetica, Arial, sans-serif", radius: 8,
    signature: 'Dark stage with bright-green highlights and bold poster cards.' },
  // Tickstream — Stripe: deep navy, indigo CTA, tabular data.
  tickstream: { brand: 'Stripe', name: 'Tickstream', kind: 'Financial',
    accent: '#635bff', accent2: '#ea2261', bg: '#1c1e54', surface: '#ffffff',
    muted: '#64748d', border: '#e3e8ee', headingFont: "'Helvetica Neue', system-ui, Arial, sans-serif", radius: 12,
    signature: 'Deep-navy masthead, blurple accents, precise tabular market data.' },
  // Formulary One — Wise: sage canvas, lime CTA, forest ink.
  formularyone: { brand: 'Wise', name: 'Formulary One', kind: 'Healthcare',
    accent: '#2f7a3e', accent2: '#163300', bg: '#163300', surface: '#ffffff',
    muted: '#6b7280', border: '#d2dacb', headingFont: "'Helvetica Neue', Arial, sans-serif", radius: 18,
    signature: 'Deep-forest masthead with green coverage rows on white cards.' },
  // ShelfSense — Airbnb: white-and-ink, single coral accent.
  shelfsense: { brand: 'Airbnb', name: 'ShelfSense', kind: 'Retail Ops',
    accent: '#ff385c', accent2: '#460479', bg: '#ffffff', surface: '#ffffff',
    muted: '#6a6a6a', border: '#dddddd', headingFont: "'Helvetica Neue', Helvetica, Arial, sans-serif", radius: 14,
    signature: 'Light, friendly cards with a single coral accent and hairline dividers.' },
  // Reel Rights — Runway ML: cinematic black, slate neutrals, image-first.
  reelrights: { brand: 'Runway', name: 'Reel Rights', kind: 'Media',
    accent: '#111111', accent2: '#767d88', bg: '#000000', surface: '#ffffff',
    muted: '#767d88', border: '#c9ccd1', headingFont: "'Helvetica Neue', Arial, sans-serif", radius: 8,
    signature: 'Pure-black masthead, colorless editorial cards — the asset IS the card.' },
  // GridPulse — Tesla: carbon dark, single electric-blue accent, sharp corners.
  gridpulse: { brand: 'Tesla', name: 'GridPulse', kind: 'Energy',
    accent: '#3e6ae1', accent2: '#e31937', bg: '#171a20', surface: '#ffffff',
    muted: '#5a5f66', border: '#e0e0e0', headingFont: "'Helvetica Neue', Arial, sans-serif", radius: 4,
    signature: 'Carbon-dark showroom masthead, electric-blue accent, sharp white readouts.' },
  // InferHub — Claude: warm charcoal, coral CTA, Garamond headings, cream cards.
  inferhub: { brand: 'Claude', name: 'InferHub', kind: 'ISV / ML',
    accent: '#cc785c', accent2: '#3d9b8a', bg: '#181715', surface: '#faf9f5',
    muted: '#6b6862', border: '#e6dfd8', headingFont: "'Cormorant Garamond', Georgia, 'Times New Roman', serif", radius: 12,
    signature: 'Warm charcoal masthead, coral accent, editorial serif headings over cream cards.' },
  // DataDrop — Supabase: near-black, emerald accent, monospace data feel.
  datadrop: { brand: 'Supabase', name: 'DataDrop', kind: 'Data',
    accent: '#10916a', accent2: '#054cff', bg: '#1c1c1c', surface: '#ffffff',
    muted: '#707070', border: '#dfdfdf', headingFont: "Inter, 'Helvetica Neue', Helvetica, Arial, sans-serif", radius: 6,
    signature: 'Charcoal masthead with emerald accents and hairline dataset cards.' },
  // Concierge.io — Starbucks: house-green masthead, gold ceremony accent.
  conciergeio: { brand: 'Starbucks', name: 'Concierge.io', kind: 'Hospitality',
    accent: '#00754a', accent2: '#b08d3c', bg: '#1e3932', surface: '#ffffff',
    muted: '#5c6b66', border: '#e7e7e7', headingFont: "Georgia, 'Times New Roman', serif", radius: 12,
    signature: 'Espresso house-green masthead, green pill CTAs, gold ceremony accents.' },
};

export const THEMES = Object.fromEntries(
  Object.entries(RAW).map(([id, t]) => [id, { ...t, ink: inkFor(t.bg), dark: luminance(t.bg) < 0.5 }]),
);

const FALLBACK = {
  brand: '', name: 'Service', kind: 'Service',
  accent: '#0972d3', accent2: '#5f6b7a', bg: '#0f1b2d', surface: '#ffffff',
  ink: '#ffffff', muted: '#8a96a3', border: '#e9ebed',
  headingFont: "'Helvetica Neue', Helvetica, Arial, sans-serif", radius: 10, dark: true,
  signature: 'Generic pay-per-call API.',
};

export function getTheme(sellerId) {
  return THEMES[sellerId] || FALLBACK;
}
